import { ComponentProps, FC, ReactNode } from 'react';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { useRouter } from 'next/router';

/**
 * LocalizationProvider used to wrap the whole app in _app.tsx, which pulled
 * @mui/x-date-pickers and the Luxon adapter into the shared bundle: 23 kB of
 * First Load JS on every route, including the ones with no date field at all
 * (measured — the home page went 450 kB -> 427 kB with the provider removed).
 *
 * Only seven components render a picker, all of them behind a dialog, the user
 * area, or /private-booking. Providing the context at the picker instead of at
 * the root keeps the dependency in those route chunks. The provider is just
 * React context, so one per picker costs nothing.
 */
const WithLocalization: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale } = useRouter();

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
      {children}
    </LocalizationProvider>
  );
};

export const DatePicker: FC<ComponentProps<typeof MuiDatePicker>> = props => (
  <WithLocalization>
    <MuiDatePicker {...props} />
  </WithLocalization>
);

export const TimePicker: FC<ComponentProps<typeof MuiTimePicker>> = props => (
  <WithLocalization>
    <MuiTimePicker {...props} />
  </WithLocalization>
);
